/**
 * PLM Statistical QC — the charts page (prototype v8's `#chartsView`).
 *
 * Layout and behaviour follow PLM-Analysis-v8.html exactly: a segmented QC-type
 * selector (Inter / Intra / Standard Reads), period + analyst + ND-toggle
 * filters, six stat tiles, an error control chart with mean / ±2σ / ±3σ limit
 * lines and colour-coded points, a data table, the deviations it found,
 * precision split at 15%, and the qualitative errors a control chart cannot show.
 *
 * All statistics live in {@link plmQcStats}; this file only renders them.
 *
 * DATA NOTE: this reads the lab-wide QC history from the DB
 * (AI_Plm_QcHistory_SP), so the point COUNT reflects what has been entered in
 * the LIMS — not the fuller history the prototype embeds from the lab's
 * spreadsheets. "Standard Reads" plots the submitted Daily QC reads (v13,
 * 2026-09-28): each analyst's daily blind reference-slide read against the
 * slide's reference value (dailyQcPoints).
 */
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useHotkeys, formatCombo, effectiveCombo } from '../../../components/hotkeys';
import {
  CartesianGrid, Line, ComposedChart, ReferenceLine, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';
import { AppToolbar } from '../../../components/AppToolbar';
import { LoadTimeChip } from '../../../components/LoadTimeChip';
import { LoaderSymbol } from '../../../components/loading';
import { useNotify } from '../../../components/notifications';
import { fmtLocalDate } from '../../../utils/datetime';
import {
  downloadPlmQcMonthlyReport, fetchPlmDailyQcList, fetchPlmQcHistory, type PlmDailyQcRow, type PlmQcHistoryRow,
} from './plm.api';
import {
  chartLimitLines, chartXTicks, chartYScale, dailyQcPoints, deviations, outsideCounts, precisionByRange,
  qcFlags, qualitativeErrors, statsOf, toPoints,
} from './plmQcStats';
import { PlmQcPlots, type QcPlot } from './PlmQcPlots';
import './plm.css';

/** Limit-line colours (prototype `.l-act` / `.l-warn` / `.l-mean`); the labels
 *  are all one grey (`.lim`). */
const LINE_STROKE = { action: '#c0392b', warning: '#d9922e', mean: '#6b7280' } as const;
/** Axis tick text (prototype `#chSvg .ax`). */
const AXIS_TICK = { fill: '#6b7280', fontSize: 11 };

/** The three QC types, with the prototype's captions + subtitles. */
const QC_TYPES = [
  { id: 'Inter', label: 'Inter-dup', sub: 'cross-check — 2nd analyst, blind' },
  { id: 'Intra', label: 'Intra-dup', sub: 'self-check — same analyst' },
  { id: 'Std', label: 'Standard Reads', sub: 'reference slides — daily calibration' },
] as const;
type QcTypeId = typeof QC_TYPES[number]['id'];

/** The v9 plot selector (prototype v13 `PLOTS`). */
const PLOTS = [
  { id: 'control', label: 'Control chart' },
  { id: 'youden', label: 'Youden plot' },
  { id: 'diff', label: 'Difference vs concentration' },
  { id: 'rel', label: 'Relative difference' },
] as const;

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** yyyymm key of an ISO date. */
const monthKey = (iso: string | null) => {
  if (!iso) return 0;
  const d = new Date(iso);
  return d.getFullYear() * 100 + d.getMonth() + 1;
};
const monthLabel = (k: number) => `${MON[(k % 100) - 1]} ${Math.floor(k / 100)}`;

type Props = {
  onBack?: () => void;
  /** Open on this QC type — Daily QC opens on Standard Reads. */
  initialType?: QcTypeId;
};

export function PlmQcChartsPage({ onBack, initialType = 'Inter' }: Props) {
  const notify = useNotify();
  const [rows, setRows] = useState<PlmQcHistoryRow[]>([]);
  const [daily, setDaily] = useState<PlmDailyQcRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  // The load-time chip docks in the app top bar, like the entry page's does
  // (prototype v8 keeps it on the charts view too). The entry page's PlmTopbarMeta
  // is unmounted while the charts are open, so the slot is free.
  const [metaSlot, setMetaSlot] = useState<HTMLElement | null>(null);
  useEffect(() => { setMetaSlot(document.getElementById('ai-topbar-meta')); }, []);
  const [type, setType] = useState<QcTypeId>(initialType);
  const [period, setPeriod] = useState<string>('all');
  const [analyst, setAnalyst] = useState('all');
  // Prototype v8: ND/ND pairs are plotted (as 0-error points) but excluded from
  // the σ/limit maths by default, because a 0/0 check flatters precision.
  const [excludeNd, setExcludeNd] = useState(true);
  const [showTable, setShowTable] = useState(false);
  // v9 plot selector + the 0–100 / 0–20 % zoom (prototype v13 `chartState.plot`
  // / `.zoom`). They persist across a QC-type switch, as the prototype's do.
  const [plotSel, setPlotSel] = useState<'control' | QcPlot>('control');
  const [zoom, setZoom] = useState<100 | 20>(100);

  const load = async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const [h, d] = await Promise.all([fetchPlmQcHistory(), fetchPlmDailyQcList()]);
      setRows(h);
      setDaily(d);
    } catch {
      notify.error('Could not read the QC history. Please try again.');
    } finally {
      setLoading(false);
      setLoadMs(performance.now() - t0);
    }
  };
  useEffect(() => { void load(); }, []);

  const isRef = type === 'Std';
  // Standard reads have no relative difference (their "mean" is the reference);
  // the prototype falls back to the control chart.
  const plot = isRef && plotSel === 'rel' ? 'control' : plotSel;
  const plotOptions = PLOTS.filter(p => !(isRef && p.id === 'rel'))
    .map(p => ({ ...p, label: isRef && p.id === 'diff' ? 'Reference error' : p.label }));
  const allPoints = useMemo(() => [...toPoints(rows), ...dailyQcPoints(daily)], [rows, daily]);

  // Every point of the selected TYPE (before the period / analyst / ND filters),
  // so the analyst + period pickers list only what is relevant.
  const typePoints = useMemo(
    () => allPoints.filter(p => p.kind === type), [allPoints, type]);

  const analysts = useMemo(() => {
    const set = new Set<string>();
    for (const p of typePoints) {
      if (p.analyst) set.add(p.analyst);
      // A standard read's READ 1 is the slide's reference, not an analyst.
      if (p.analystOriginal && p.kind !== 'Std') set.add(p.analystOriginal);
    }
    return [...set].sort();
  }, [typePoints]);

  // The months present for this type, newest last — feeds the Period dropdown
  // and the "rolling 3" window.
  const months = useMemo(
    () => [...new Set(typePoints.map(p => monthKey(p.date)).filter(Boolean))].sort((a, b) => a - b),
    [typePoints]);

  // The WINDOW: type + period + analyst (the prototype's `filtered().all`).
  const windowPoints = useMemo(() => {
    let ps = typePoints;
    if (period === 'roll3') {
      const keep = new Set(months.slice(-3));
      ps = ps.filter(p => keep.has(monthKey(p.date)));
    } else if (period !== 'all') {
      ps = ps.filter(p => monthKey(p.date) === Number(period));
    }
    if (analyst !== 'all') {
      ps = ps.filter(p => p.analyst === analyst || p.analystOriginal === analyst);
    }
    return ps;
  }, [typePoints, period, analyst, months]);

  // The PLOTTED set (the prototype's `pts`): with "Exclude ND / ND" on, those
  // pairs leave EVERYTHING — plot, tiles, table, deviations, precision — not just
  // the limit maths. They are only counted in the Points tile's sub-line.
  const points = useMemo(
    () => (excludeNd ? windowPoints.filter(p => !p.ndPair) : windowPoints),
    [windowPoints, excludeNd]);
  const stats = useMemo(() => statsOf(points), [points]);
  const outside = useMemo(() => outsideCounts(points, stats), [points, stats]);
  const devs = useMemo(() => deviations(points, stats, isRef), [points, stats, isRef]);
  const bands = useMemo(() => precisionByRange(points), [points]);
  // Qualitative errors follow the SAME window (type / period / analyst, ND/ND
  // included) — the prototype's `qualRows(all, type)`, not the whole lab.
  const qual = useMemo(
    () => qualitativeErrors(rows, new Set(windowPoints.map(p => p.sequenceOid))),
    [rows, windowPoints]);

  // Colour a point by where it sits relative to the limits (prototype: blue
  // normal, amber outside warning, red outside action).
  const sevOf = (err: number): 'ok' | 'warn' | 'act' => {
    if (!stats || !stats.sd) return 'ok';
    const d = Math.abs(err - stats.mean);
    return d > 3 * stats.sd ? 'act' : d > 2 * stats.sd ? 'warn' : 'ok';
  };

  const chartData = points.map((p, i) => ({
    i: i + 1,
    error: p.error,
    sev: sevOf(p.error),
    label: `${p.sampleLayer ?? ''} · ${p.name}`,
    date: p.date ? fmtLocalDate(p.date) : '',
    // For the hover tooltip (prototype v8): the sample/slide and both reads.
    slide: p.sampleLayer ?? '—',
    read1: p.analystOriginal ?? '—',
    result1: p.result1,
    read2: p.analyst ?? '—',
    result2: p.result2,
  }));

  // The prototype's drawChart scale: Y spans the points AND the action limits
  // (else the ±3σ lines sit off-screen), gridlines at nice steps; X is the point
  // NUMBER, first point on the left edge, last on the right (a lone point
  // centred), dated at up to six evenly spread points.
  const yScale = useMemo(() => chartYScale(points.map(p => p.error), stats), [points, stats]);
  const xTicks = useMemo(() => chartXTicks(points.length), [points.length]);
  const xDomain: [number, number] = points.length === 1 ? [0, 2] : [1, Math.max(1, points.length)];

  const typeMeta = QC_TYPES.find(t => t.id === type)!;
  // "Export monthly report" covers the month picked in Period, else this month.
  const reportMonth = /^\d{6}$/.test(period)
    ? `${period.slice(0, 4)}-${period.slice(4)}`
    : `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  const reportMonthLabel = monthLabel(Number(reportMonth.replace('-', '')));
  const [exporting, setExporting] = useState(false);
  // Keyboard (owner 2026-10-19): Esc goes back, Alt+X exports the monthly report.
  useHotkeys([
    { combo: 'esc', label: 'Back', group: 'PLM QC Charts', order: 0, allowInInput: false, disabled: !onBack, run: () => onBack?.() },
    { combo: 'alt+x', label: 'Export monthly report', group: 'PLM QC Charts', order: 1, disabled: exporting,
      run: () => { void exportMonthly(); } },
  ]);
  const exportMonthly = async () => {
    setExporting(true);
    try {
      await downloadPlmQcMonthlyReport(reportMonth);
      notify.success('Monthly QC report exported to PDF successfully.');
    } catch {
      notify.error('Could not export the monthly QC report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const periodLabel = period === 'all' ? 'All time'
    : period === 'roll3' ? 'Rolling 3 months' : monthLabel(Number(period));

  // The prototype's number formats, verbatim: mean error to 2 dp with a sign, σ
  // to 2 dp, every limit to 1 dp (the chart's limit labels are built in
  // chartLimitLines; the data table's BTLIMS columns in qcFlags).
  const f2 = (v: number) => `${v > 0 ? '+' : ''}${v.toFixed(2)}`;

  return (
    <div className="plm-root plm-charts">
      {/* Load-time chip in the app top bar (prototype v8 keeps it on the charts
          view too). */}
      {metaSlot && createPortal(<LoadTimeChip ms={loadMs} loading={loading} />, metaSlot)}
      <AppToolbar
        left={
          <button className="ca-btn" onClick={onBack} title={`Back to the batch (${formatCombo(effectiveCombo('esc'))})`}>
            ← Back to PLM entry
          </button>
        }
        right={
          <span className="plm-toolbar-right">
            <button
              className="ca-btn"
              onClick={() => { void exportMonthly(); }}
              disabled={exporting}
              title={`Export the monthly PLM QC report (PDF) — ${reportMonthLabel} (${formatCombo(effectiveCombo('alt+x'))})`}
            >Export monthly report</button>
          </span>
        }
      />

      <div className="plm-charts__head">
        <h2>PLM Statistical QC</h2>
        <p className="plm-charts__sub">{typeMeta.sub}</p>
      </div>

      <div className="plm-charts__filters">
        <span className="plm-charts__seg" role="tablist" aria-label="QC type">
          {QC_TYPES.map(t => (
            <button
              key={t.id}
              role="tab"
              aria-selected={type === t.id}
              className={`plm-charts__segbtn${type === t.id ? ' is-active' : ''}`}
              onClick={() => { setType(t.id); setAnalyst('all'); setPeriod('all'); }}
            >{t.label}</button>
          ))}
        </span>
        <label>
          Period
          <select className="plm-input" value={period} onChange={e => setPeriod(e.target.value)}>
            <option value="all">All time</option>
            <option value="roll3">Rolling 3 months</option>
            {[...months].reverse().map(m => (
              <option key={m} value={String(m)}>{monthLabel(m)}</option>
            ))}
          </select>
        </label>
        <label>
          Analyst
          <select className="plm-input" value={analyst} onChange={e => setAnalyst(e.target.value)}>
            <option value="all">{type === 'Inter' ? 'Lab overall (pooled)' : 'All'}</option>
            {analysts.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <label className="plm-charts__chk"
          title="ND/ND pairs always check at 0/0 and make the σ look tighter than it is">
          <input type="checkbox" checked={excludeNd}
            onChange={e => setExcludeNd(e.target.checked)} />
          Exclude ND / ND pairs from the limits
        </label>
      </div>

      {loading ? (
        <div className="plm-qc__loading"><LoaderSymbol /><span>Reading QC history…</span></div>
      ) : isRef && typePoints.length === 0 ? (
        <div className="plm-charts__card">
          <div className="plm-qc__empty">
            No Daily QC read has been submitted yet — each analyst's daily reference-slide
            read (PLM → QC → + New QC → Reference) is plotted here against the slide's reference.
          </div>
        </div>
      ) : (
        <>
          {/* Tiles (prototype `#chTiles`): with no points the whole row is ONE
              "No points for this filter." line, not six dashes. */}
          {stats ? (
            <div className="plm-charts__tiles">
              <Tile label="Points" value={String(stats.n)}
                sub={`${windowPoints.length} QC reads`
                  + (excludeNd ? ` · ${windowPoints.length - points.length} ND/ND excluded` : '')} />
              <Tile label="Mean error" value={`${f2(stats.mean)}%`}
                sub={isRef ? 'read − reference' : 're-read − original'} />
              <Tile label="Std. deviation" value={`${stats.sd.toFixed(2)}%`} sub="σ — precision" />
              <Tile label="Warning limits"
                value={`${stats.warnLo.toFixed(1)} … ${stats.warnHi.toFixed(1)}`} sub="mean ± 2σ" />
              <Tile label="Action limits"
                value={`${stats.actionLo.toFixed(1)} … ${stats.actionHi.toFixed(1)}`} sub="mean ± 3σ" />
              <Tile label="Outside limits"
                value={`${outside.warning} / ${outside.action}`} sub="warning / action"
                tone={outside.action ? 'bad' : outside.warning ? 'warn' : undefined} />
            </div>
          ) : (
            <div className="plm-charts__tilesempty">No points for this filter.</div>
          )}

          <div className="plm-charts__card">
            {/* v9 plot selector, above the title (prototype v13 `.plotbar`). The
                zoom only applies to the Youden and difference plots. */}
            <div className="plm-charts__plotbar">
              <span className="plm-charts__seg" role="tablist" aria-label="Plot">
                {plotOptions.map(p => (
                  <button key={p.id} role="tab" aria-selected={plot === p.id}
                    className={`plm-charts__segbtn${plot === p.id ? ' is-active' : ''}`}
                    onClick={() => setPlotSel(p.id)}>{p.label}</button>
                ))}
              </span>
              {(plot === 'youden' || plot === 'diff') && (
                <span className="plm-charts__seg" role="tablist" aria-label="Zoom">
                  {([100, 20] as const).map(z => (
                    <button key={z} role="tab" aria-selected={zoom === z}
                      className={`plm-charts__segbtn${zoom === z ? ' is-active' : ''}`}
                      onClick={() => setZoom(z)}>0–{z}%</button>
                  ))}
                </span>
              )}
            </div>
            <div className="plm-charts__title">
              {typeMeta.label} — error control chart · {periodLabel}
              {analyst !== 'all' ? ` · ${analyst}` : ''}
            </div>
            {/* The legend and the data table stay on screen even with nothing to
                plot (prototype v8); only the plot area says "No data". */}
            <>
                {plot !== 'control' ? (
                  <PlmQcPlots points={points} plot={plot} zoom={zoom} analyst={analyst} isRef={isRef} />
                ) : !stats || points.length === 0 ? (
                  <div className="plm-charts__nodata">No data</div>
                ) : (
                <div className="plm-charts__plot">
                  {/* Prototype `#chSvg` (320px, margins l46 r118 t14 b34):
                      horizontal gridlines only, no axis lines or tick marks, grey
                      11px tick text, limit labels in the right margin. */}
                  <ResponsiveContainer width="100%" height={320}>
                    <ComposedChart data={chartData}
                      margin={{ top: 14, right: 118, bottom: 0, left: 0 }}>
                      <CartesianGrid stroke="#eef0f3" vertical={false} />
                      <XAxis dataKey="i" type="number" domain={xDomain} ticks={xTicks}
                        interval={0} height={34} axisLine={false} tickLine={false}
                        tick={AXIS_TICK} tickMargin={10}
                        tickFormatter={(i: number) => chartData[i - 1]?.date ?? ''} />
                      <YAxis type="number" domain={yScale.domain} ticks={yScale.ticks}
                        interval={0} width={46} axisLine={false} tickLine={false}
                        tick={AXIS_TICK} tickMargin={6} allowDataOverflow
                        tickFormatter={(v: number) => String(Number(v.toFixed(2)))} />
                      <Tooltip content={<ChartTooltip />} isAnimationActive={false}
                        cursor={{ stroke: '#9aa2af', strokeWidth: 1, strokeDasharray: '3 3' }} />
                      {/* Action · Warning · Mean · Warning · Action, labelled to
                          1 dp in the prototype's one grey (`.lim`). With σ 0 only
                          the Mean — the five coincide and would overprint. */}
                      {chartLimitLines(stats).map((l, k) => (
                        <ReferenceLine key={`${l.kind}-${k}`} y={l.y}
                          stroke={LINE_STROKE[l.kind]} strokeWidth={1.5}
                          strokeDasharray={l.kind === 'mean' ? undefined : '6 4'}
                          label={{ value: l.label, position: 'right', offset: 6,
                            fill: '#434b5a', fontSize: 11, fontWeight: 600 }} />
                      ))}
                      <Line type="linear" dataKey="error" stroke="#4aa3c7" strokeWidth={1.5}
                        strokeOpacity={0.55} isAnimationActive={false}
                        dot={ControlDot} activeDot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                )}
                {plot === 'control' && (
                  <div className="plm-charts__legend">
                    <span><i className="plm-lg plm-lg--pt" />Error (re-read − original)</span>
                    <span><i className="plm-lg plm-lg--mean" />Mean</span>
                    <span><i className="plm-lg plm-lg--warn" />Warning ± 2σ</span>
                    <span><i className="plm-lg plm-lg--act" />Action ± 3σ</span>
                    <span><i className="plm-lg plm-lg--pt is-warn" />outside warning</span>
                    <span><i className="plm-lg plm-lg--pt is-act" />outside action</span>
                  </div>
                )}

                {/* The BTLIMS columns (prototype v13): both reads split into
                    analyst · type · %, then (r1+r2)/2 · r1−r2 · ACM1 · ACM2 ·
                    FlagType · FlagACM · Category · rel.diff. */}
                <details className="plm-charts__data" open={showTable}
                  onToggle={e => setShowTable((e.target as HTMLDetailsElement).open)}>
                  <summary>Data table · {points.length} point{points.length === 1 ? '' : 's'}</summary>
                  <div className="plm-charts__tablewrap">
                    <table className="plm-qc__table">
                      <thead><tr>
                        <th className="num">#</th><th>Date</th><th>Sample / slide</th>
                        <th>Analyst 1</th><th>Type1</th><th className="num">Result1</th>
                        <th>Analyst 2</th><th>Type2</th><th className="num">Result2</th>
                        <th className="num">(r1+r2)/2</th><th className="num">r1−r2</th>
                        <th>ACM1</th><th>ACM2</th><th>FlagType</th><th>FlagACM</th>
                        <th className="num">Category</th><th className="num">rel.diff %</th>
                      </tr></thead>
                      <tbody>
                        {points.map((p, i) => {
                          const fl = qcFlags(p, isRef);
                          return (
                            <tr key={p.sequenceOid + i}
                              className={sevOf(p.error) === 'act' ? 'plm-charts__rowact'
                                : sevOf(p.error) === 'warn' ? 'plm-charts__rowwarn' : ''}>
                              <td className="num">{i + 1}</td>
                              <td>{p.date ? fmtLocalDate(p.date) : '—'}</td>
                              <td>{p.sampleLayer ?? '—'}</td>
                              <td>{p.analystOriginal ?? '—'}</td>
                              <td>{p.type1}</td>
                              <td className="num">{p.pct1}</td>
                              <td>{p.analyst ?? '—'}</td>
                              <td>{p.type2}</td>
                              <td className="num">{p.pct2}</td>
                              <td className="num">{fl.mean}</td>
                              <td className="num">{fl.d}</td>
                              <td>{fl.acm1}</td>
                              <td>{fl.acm2}</td>
                              <td className={fl.flagType ? 'plm-charts__fl' : ''}>{fl.flagType}</td>
                              <td className={fl.flagAcm ? 'plm-charts__fl' : ''}>{fl.flagAcm}</td>
                              <td className="num">{fl.category}</td>
                              <td className="num">{fl.rel ?? ''}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </details>
            </>
          </div>

          <div className="plm-charts__grid2">
            <div className="plm-charts__card">
              <div className="plm-charts__title">
                Deviations
                <span className="plm-charts__muted"> — must be investigated, corrective action written up</span>
              </div>
              {devs.length === 0 ? (
                // Prototype v8 `li.ok`, shown whenever no rule fired: a clean
                // window is a RESULT, said with a green tick and the reason — not a
                // grey "nothing here" box, which read as missing data (owner
                // report 2026-09-26). Verbatim prototype text.
                <ul className="plm-charts__devs">
                  <li className="is-ok">
                    <span className="plm-charts__sev" aria-hidden="true">✓</span>
                    <span>
                      No deviations — all points inside the action limit, no successive
                      warning points.
                    </span>
                  </li>
                </ul>
              ) : (
                <ul className="plm-charts__devs">
                  {devs.map((d, i) => (
                    <li key={`${d.index}-${i}`} className={`is-${d.severity}`}>
                      <span className="plm-charts__sev">{d.severity === 'action' ? '■' : '▲'}</span>
                      <span>
                        {d.message}
                        <span className="plm-charts__devsub">
                          {d.point.date ? fmtLocalDate(d.point.date) : ''} · {d.point.sampleLayer ?? ''}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="plm-charts__card">
              <div className="plm-charts__title">
                Precision by range
                <span className="plm-charts__muted"> — low range is the critical one</span>
              </div>
              <table className="plm-qc__table">
                <thead><tr>
                  <th>Range</th><th className="num">Points</th>
                  <th className="num">σ</th><th className="num">Warning</th>
                </tr></thead>
                <tbody>
                  {bands.map(b => (
                    <tr key={b.label}>
                      <td>{b.label}</td>
                      <td className="num">{b.n}</td>
                      {/* Prototype: σ to 2 dp ("0.29", "0.00"), warning to 1 dp. */}
                      <td className="num">{b.sd == null ? '—' : b.sd.toFixed(2)}</td>
                      <td className="num">{b.warning == null ? '—' : `± ${b.warning.toFixed(1)}`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="plm-charts__note plm-charts__note--plain">
                One σ over the full 0–100% range hides low-range problems (absolute
                differences understate them; relative ones overstate them). Tracking
                the two ranges separately is the documented strategy.
              </p>
            </div>
          </div>

          <div className="plm-charts__card">
            <div className="plm-charts__title">Qualitative errors</div>
            <table className="plm-qc__table">
              <thead><tr>
                <th>Analyst</th><th className="num">QC reads</th>
                <th className="num">ACM disagreements</th>
                <th className="num">Asbestos-type disagreements</th>
              </tr></thead>
              <tbody>
                {qual.length === 0 ? (
                  <tr><td colSpan={4} className="plm-final__empty">No reads.</td></tr>
                ) : qual.map(q => (
                  <tr key={q.analyst}>
                    <td>{q.analyst}</td>
                    <td className="num">{q.pairs}</td>
                    <td className={`num${q.classificationErrors ? ' plm-charts__bad' : ''}`}>
                      {q.classificationErrors}
                    </td>
                    <td className={`num${q.typeErrors ? ' plm-charts__bad' : ''}`}>
                      {q.typeErrors}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="plm-charts__note plm-charts__note--plain">
              For re-reads the true result is unknown, so a disagreement is only
              classified as Type I / Type II after it is resolved (re-check in-house
              or send out for PLM/TEM). Any qualitative disagreement is a deviation.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

/** The chart hover card (prototype v8's `.ch-tip`): point #, date, sample/slide,
 *  both reads, and the signed error. */
type TipPoint = {
  i: number; date: string; slide: string;
  read1: string; result1: string; read2: string; result2: string; error: number;
};
function ChartTooltip({ active, payload }: {
  active?: boolean; payload?: Array<{ payload: TipPoint }>;
}) {
  if (!active || !payload?.length) return null;
  const d = payload[0]!.payload;
  return (
    <div className="plm-charts__tip">
      <b>#{d.i} · {d.date}</b>
      <div>{d.slide}</div>
      <div>{d.read1}: {d.result1}</div>
      <div>{d.read2}: {d.result2}</div>
      <div className="plm-charts__tiperr">Error <b>{d.error > 0 ? '+' : ''}{d.error}</b></div>
    </div>
  );
}

/** A control-chart point, coloured by where it sits relative to the limits. */
function ControlDot(props: { cx?: number; cy?: number; payload?: { sev?: string } }) {
  const { cx, cy, payload } = props;
  if (cx == null || cy == null) return <g />;
  const sev = payload?.sev;
  const fill = sev === 'act' ? '#c0392b' : sev === 'warn' ? '#d9922e' : '#4aa3c7';
  const r = sev === 'ok' ? 3.5 : 5;
  return <circle cx={cx} cy={cy} r={r} fill={fill} stroke="#fff" strokeWidth={1.5} />;
}

function Tile({ label, value, sub, tone }: {
  label: string; value: string; sub?: string; tone?: 'warn' | 'bad';
}) {
  return (
    <div className={`plm-charts__tile${tone ? ` plm-charts__tile--${tone}` : ''}`}>
      <div className="plm-charts__tilelabel">{label}</div>
      <div className="plm-charts__tilevalue">{value}</div>
      {sub && <div className="plm-charts__tilesub">{sub}</div>}
    </div>
  );
}
